import { useEffect, useMemo } from 'react';

import { SearchSuggests, type Suggest, type Category } from '@components/SearchSuggests';

import { useSearchSuggestsQuery } from './gql/graphql.v5.generated';
import { useSearchSuggestsInCategoriesQuery } from './gql/graphql.v3.generated';

const MAX_SUGGESTS_LIMIT = 10;
const MAX_SUGGESTS_LIMIT_IF_CATEGORIES_EXISTS = 5;
const MAX_CATEGORIES_SUGGESTS_LIMIT = 3;

export type { Suggest, Category };

export interface OnSaggestsPayload {
  suggests: Suggest[],
  categoriesSuggests: Category[],
  suggestsInCategories: Category[],
}

interface Props {
  className?: string,
  searchText?: string,
  catalogSlug?: string,
  collectionSlug?: string,
  onSuggests: (payload: OnSaggestsPayload) => void,
  onSuggestClick: (suggest: Suggest) => void,
  onCategorySuggestClick?: (category: Category) => void,
  onSuggestInCategoryClick?: (category: Category) => void,
}

export const SearchSuggestsContainer: React.FC<Props> = ({
  className,
  searchText = '',
  catalogSlug,
  collectionSlug,
  onSuggests,
  onSuggestClick,
  onCategorySuggestClick,
  onSuggestInCategoryClick,
}) => {
  const {
    data: suggestsData,
    previousData: previousSuggestsData,
  } = useSearchSuggestsQuery({
    variables: {
      searchText,
      catalogSlug,
      dynamicCollectionSlug: collectionSlug,
      suggestsLimit: MAX_SUGGESTS_LIMIT,
      categoriesSuggestsLimit: MAX_CATEGORIES_SUGGESTS_LIMIT,
    },
    context: {
      v5: true,
    },
    skip: !searchText,
  });

  const {
    data: suggestsInCategoriesData,
    previousData: previousSuggestsInCategoriesData,
  } = useSearchSuggestsInCategoriesQuery({
    variables: {
      searchText,
      pageNum: 1,
      first: 1,
    },
    skip: !searchText,
  });

  const suggests = useMemo(() => {
    const leafCategories = (suggestsInCategoriesData || previousSuggestsInCategoriesData)
      ?.products?.leafCategories?.slice(0, MAX_CATEGORIES_SUGGESTS_LIMIT) || [];
    const categoriesSuggests = (suggestsData || previousSuggestsData)?.suggestCatalogs || [];
    const suggestsInCategories = leafCategories?.map((category) => category.catalog) || [];
    let suggests = (suggestsData || previousSuggestsData)?.searchSuggestions.slice(0, MAX_SUGGESTS_LIMIT) || [];

    if (categoriesSuggests.length || suggestsInCategories.length) {
      suggests = suggests.slice(0, MAX_SUGGESTS_LIMIT_IF_CATEGORIES_EXISTS);
    }

    return {
      suggests,
      categoriesSuggests,
      suggestsInCategories,
    };
  }, [suggestsInCategoriesData, previousSuggestsInCategoriesData, suggestsData, previousSuggestsData]);
  
  useEffect(() => {
    onSuggests({
      suggests: suggests.suggests,
      categoriesSuggests: suggests.categoriesSuggests,
      suggestsInCategories: suggests.categoriesSuggests,
    })
  }, [onSuggests, suggests.categoriesSuggests, suggests.suggests]);

  if (
    !suggests.suggests.length
    && !suggests.suggestsInCategories.length
    && !suggests.categoriesSuggests.length
  ) {
    return null;
  }

  return (
    <SearchSuggests
      className={className}
      searchText={searchText}
      suggests={suggests.suggests}
      categoriesSuggests={suggests.categoriesSuggests}
      suggestsInCategories={suggests.suggestsInCategories}
      onSuggestClick={onSuggestClick}
      onCategorySuggestClick={onCategorySuggestClick}
      onSuggestInCategoryClick={onSuggestInCategoryClick}
    />
  );
}
